import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  buildDeckProgressMap,
  progressLegend,
  progressGeometry,
  progressPaletteFromCss,
} from '../lib/deck-progress.ts';
import type { DeckHistory } from '../lib/deck-page.ts';
import { deckPath } from '../lib/decks.ts';
import type { DeckScope } from '../../shared/deck.ts';
import { cn } from '../lib/utils.ts';
import { SectionHeading } from './SectionHeading.tsx';

function ProgressMark({
  colors,
  dataCardId,
  height,
  rotation = 0,
  unlocked = true,
  width,
}: {
  colors: { fill: string; stroke: string };
  dataCardId?: string;
  height: number;
  rotation?: number;
  unlocked?: boolean;
  width: number;
}) {
  return (
    <svg
      className="block shrink-0 overflow-visible"
      data-card-id={dataCardId}
      opacity={unlocked ? undefined : 0.28}
      height={height}
      style={{
        height,
        transform: rotation ? `rotate(${rotation}deg)` : undefined,
        transformBox: 'fill-box',
        transformOrigin: 'center',
        width,
      }}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
    >
      <rect
        fill={colors.fill}
        height={height}
        rx={progressGeometry.markRadius}
        stroke={colors.stroke}
        strokeWidth={progressGeometry.markStrokeWidth}
        width={width}
      />
    </svg>
  );
}

function HierarchyText({
  children,
  current,
  href,
  unlocked = true,
}: {
  children: string;
  current: boolean;
  href: string;
  unlocked?: boolean;
}) {
  return current || !unlocked ? (
    <span className={!unlocked ? 'text-muted-foreground' : undefined}>
      {children}
    </span>
  ) : (
    <a href={href}>{children}</a>
  );
}

export function DeckProgressMap({
  deckSlug,
  history,
  scope = {},
}: {
  deckSlug: string;
  history: DeckHistory;
  scope?: DeckScope;
}) {
  const intl = useIntl();
  const palette = progressPaletteFromCss();
  const sections = useMemo(
    () => buildDeckProgressMap(history, Date.now(), palette),
    [
      history,
      palette.background,
      palette.border,
      palette.success,
      palette.successSurface,
      palette.recent,
      palette.recentSurface,
      palette.aging,
      palette.agingSurface,
      palette.destructive,
      palette.errorSurface,
    ],
  );
  const legend = useMemo(
    () => progressLegend(Date.now(), palette),
    [
      palette.background,
      palette.border,
      palette.success,
      palette.successSurface,
      palette.recent,
      palette.recentSurface,
      palette.aging,
      palette.agingSurface,
      palette.destructive,
      palette.errorSurface,
    ],
  );

  return (
    <section className="grid min-w-0 gap-4">
      <SectionHeading
        title={intl.formatMessage({ id: 'web.deckProgress.title' })}
      />
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
        {legend.map((item) => (
          <span className="inline-flex items-center gap-1.5" key={item.id}>
            <ProgressMark colors={item.colors} height={12} width={12} />
            {intl.formatMessage({ id: `web.deckProgress.${item.id}` })}
          </span>
        ))}
      </div>
      <div className="grid min-w-0 gap-6">
        {sections.map((section) => (
          <section className="grid min-w-0 gap-3" key={section.id}>
            <h3 className="m-0 text-base leading-tight font-semibold">
              <HierarchyText
                current={scope.sectionSlug === section.slug && !scope.unitSlug}
                href={deckPath(deckSlug, { sectionSlug: section.slug })}
                unlocked={section.unlocked !== false}
              >
                {section.name}
              </HierarchyText>
            </h3>
            <div className="grid min-w-0 gap-5">
              {section.units.map((unit) => (
                <div className="grid min-w-0 gap-2" key={unit.id}>
                  <h4
                    className={cn(
                      'm-0 text-sm leading-tight font-semibold',
                      unit.unlocked === false && 'text-muted-foreground',
                    )}
                  >
                    <HierarchyText
                      current={scope.unitSlug === unit.slug && !scope.topicSlug}
                      href={deckPath(deckSlug, {
                        sectionSlug: section.slug,
                        unitSlug: unit.slug,
                      })}
                      unlocked={unit.unlocked !== false}
                    >
                      {unit.name}
                    </HierarchyText>
                  </h4>
                  <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                    {unit.topics.map((topic) => (
                      <div
                        className="grid w-fit max-w-full min-w-0 flex-none gap-[2px]"
                        key={topic.id}
                      >
                        <p className="m-0 text-sm">
                          <HierarchyText
                            current={scope.topicSlug === topic.slug}
                            href={deckPath(deckSlug, {
                              sectionSlug: section.slug,
                              topicSlug: topic.slug,
                              unitSlug: unit.slug,
                            })}
                            unlocked={topic.unlocked !== false}
                          >
                            {topic.name}
                          </HierarchyText>
                        </p>
                        <div className="flex min-w-0 gap-3 overflow-x-auto pb-1">
                          {topic.pages.map((page, pageIndex) => (
                            <div
                              className="grid shrink-0 border border-border bg-card"
                              data-page-index={pageIndex}
                              key={`${topic.id}-${pageIndex}`}
                              style={{
                                gridTemplateColumns: `repeat(${progressGeometry.pageColumns}, ${progressGeometry.markWidth}px)`,
                                gridTemplateRows: `repeat(${progressGeometry.pageRows}, ${progressGeometry.markHeight}px)`,
                                gap: progressGeometry.pageGap,
                                padding: progressGeometry.pagePadding,
                                borderRadius: progressGeometry.pageRadius,
                              }}
                            >
                              {page.cards.map((card, cardIndex) =>
                                card ? (
                                  <ProgressMark
                                    colors={card.colors}
                                    dataCardId={card.card.id}
                                    height={progressGeometry.markHeight}
                                    key={card.card.id}
                                    rotation={card.rotation}
                                    unlocked={card.card.unlocked !== false}
                                    width={progressGeometry.markWidth}
                                  />
                                ) : (
                                  <span
                                    className="block"
                                    key={`empty-${cardIndex}`}
                                  />
                                ),
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}
